/**
 * The four areas a project has, and where each one lives.
 *
 * One table, because the same list is read three ways: to render the header's
 * area nav, to build its links, and to decide which pill is current. The
 * sidebar used to hold hand-written copies of this, which is exactly the drift
 * a table prevents.
 *
 * Pure on purpose: no router imports, so it can be unit-tested without a
 * memory router and so the round trip between `areaHref` and
 * `areaFromPathname` is assertable.
 */
export type ProjectArea = 'planning' | 'financials' | 'team' | 'billing';

export type ProjectAreaOption = {
  area: ProjectArea;
  /**
   * The area's full i18n key. TimeLog Web's own `menuItem` key where its Danish fits;
   * Team has its own, because Web's Danish "Hold" is the wrong word here — it is "Team".
   */
  labelKey:
    | 'projectManagement:menuItem.Planning'
    | 'projectManagement:menuItem.Financials'
    | 'pmShell:projectAreaNav.Team'
    | 'projectManagement:menuItem.Billing';
  /** Material Symbols glyph name — see `components/MaterialSymbol.tsx`. */
  icon: string;
  /** The URL segment under `/projects/:projectId`. */
  segment: string;
  /**
   * Planning and Financials keep the ids the sidebar used, so every existing
   * Playwright call site resolves against the header instead.
   */
  automationId: string;
};

export const AREAS: readonly ProjectAreaOption[] = [
  {
    area: 'planning',
    labelKey: 'projectManagement:menuItem.Planning',
    icon: 'checklist',
    segment: 'planning',
    automationId: 'nav-planning',
  },
  {
    area: 'financials',
    labelKey: 'projectManagement:menuItem.Financials',
    icon: 'euro',
    segment: 'financials',
    automationId: 'nav-financials',
  },
  {
    area: 'team',
    labelKey: 'pmShell:projectAreaNav.Team',
    icon: 'groups',
    segment: 'team',
    automationId: 'nav-team',
  },
  {
    area: 'billing',
    labelKey: 'projectManagement:menuItem.Billing',
    icon: 'receipt_long',
    segment: 'billing',
    automationId: 'nav-billing',
  },
];

export type ProjectRouteParams = {
  projectId?: string;
};

/** The `/projects/:id` prefix every area link hangs off, or `null` away from a project route. */
export const projectBasePath = ({ projectId }: ProjectRouteParams): string | null =>
  projectId !== undefined && projectId !== '' ? `/projects/${projectId}` : null;

export const areaHref = (base: string, area: ProjectArea): string => {
  const option = AREAS.find((candidate) => candidate.area === area);
  return `${base}/${option?.segment ?? area}`;
};

/**
 * Which area a path is in, read from its LAST segment.
 *
 * The segment rather than link-vs-URL equality, so a redirect between routes
 * can never leave the wrong pill lit: a `NavLink`-style `isActive` compares the
 * pre-redirect `to` against the post-redirect URL (ADR 0031).
 */
export const areaFromPathname = (pathname: string): ProjectArea | null => {
  const segments = pathname.split('/').filter((segment) => segment !== '');
  const last = segments[segments.length - 1];
  if (last === undefined) return null;
  return AREAS.find((option) => option.segment === last)?.area ?? null;
};

/**
 * Project details (docs/briefs/project-details-v2/brief.md) is not a fifth
 * area either: it is reached from the header's second line, never from the
 * pills, so `areaFromPathname` reads it as no area and no pill lights there.
 */
export const DETAILS_SEGMENT = 'details';

export const detailsHref = (base: string): string => `${base}/${DETAILS_SEGMENT}`;

export const isDetailsPathname = (pathname: string): boolean => {
  const segments = pathname.split('/').filter((segment) => segment !== '');
  return segments[segments.length - 1] === DETAILS_SEGMENT;
};
